import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Alert } from "@mui/material";
import ResourcePanel, { PanelButton, PanelClearFilters, PanelScopeRow } from "../../components/ResourcePanel";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import { ITEM_KIND_STYLES } from "../../components/itemKindStyles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { deleteRights, useDeleteRights } from "../../helper/deleteRights";
import {
  selectionResumeRoute, selectionRoute, SELECTION_KINDS, SELECTION_KIND_KEYS, SELECTION_TYPE_CHOICE_ROUTE,
} from "../../helper/selectionFlow";
import { LIST_SCOPE_MINE, useListScope, withListScope } from "../../components/ListScopeToggle";
import SelectionCard from "../../components/SelectionCard";
import ProjectFilterSelect, { ALL_PROJECTS, matchesProjectFilter } from "../../components/ProjectFilterSelect";
import KindFilterSelect, { ALL_KINDS } from "../../components/KindFilterSelect";
import useProjects from "../../components/useProjects";
import { moveItems, projectErrorMessage } from "../../helper/projects";

/** Large enough to load all selections in one request. */
const SELECTION_LIST_PAGE_SIZE = 10000;

/** The type dropdown's entries: every selection type, by its selection_type value. */
/** The selections' tones: the panel band and its New button share them. */
const KIND = ITEM_KIND_STYLES.selection;
const KIND_BUTTON_SX = { bgcolor: KIND.header, "&:hover": { bgcolor: KIND.header, filter: "brightness(0.9)" } };

const TYPE_OPTIONS = SELECTION_KIND_KEYS.map((key) => ({
  value: SELECTION_KINDS[key].type, label: SELECTION_KINDS[key].navLabel,
}));

/**
 * The selections overview: every selection of every type as a card (the
 * card names its type), filtered by type and project (client-side on the
 * full list) and by the Mine/All toggle (server-side). The card is
 * SelectionCard, shared with the Projects page; the folder button in its
 * header files the selection in a project. New selections start on the
 * type choice page.
 */
function ColorSelectionHome() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState({ isLoading: false, data: [] });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteNameInput, setDeleteNameInput] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
  const [scope, setScope] = useListScope();
  const [projectFilter, setProjectFilter] = useState(ALL_PROJECTS);
  const [typeFilter, setTypeFilter] = useState(ALL_KINDS);
  // Whether the signed-in account may delete a row: its creator or an
  // administrator (who is warned in the dialog). Same rule on the server.
  const me = useDeleteRights();
  const rightsOf = (row) => deleteRights(row, me, "selection");
  const currentRights = currentRow ? rightsOf(currentRow) : null;
  const { projects, reload: reloadProjects } = useProjects();

  const fetchData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));
    try {
      // Selections are team-wide; the Mine/All toggle narrows the list to
      // the account's own via ?scope= (filtered by the backend).
      const res = await axios.get(
        withListScope(
          `api/selection/data?page=0&per_page=${SELECTION_LIST_PAGE_SIZE}`,
          scope
        )
      );
      setPageState((old) => ({ ...old, data: res.data.data || [] }));
    } catch (err) {
      console.error(err);
      setPageState((old) => ({ ...old, data: [] }));
    } finally {
      setPageState((old) => ({ ...old, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // A new selection starts with its type; an existing one is opened by its
  // kind and id in the URL.
  const onNewClick = () => navigate(SELECTION_TYPE_CHOICE_ROUTE);

  const onContinueClick = (row) => {
    navigate(selectionResumeRoute(row.id, row.status, row.selection_type));
  };

  const onOverviewClick = (row) => {
    navigate(selectionRoute(row.selection_type, row.id, "overview"));
  };

  const handleOpenDeleteDialog = (row) => {
    setCurrentRow(row);
    setDeleteNameInput("");
    setDeleteConfirmOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setDeleteNameInput("");
  };
  const deleteArmed = currentRow && deleteNameInput.trim() === currentRow.name;
  const handleConfirmDelete = async () => {
    if (currentRow && deleteArmed) {
      try {
        await axios.delete(`api/selection/delete/${currentRow.id}/`);
        setDeleteErrorMessage(null);
      } catch (err) {
        // 409: the selection is a reproduction's palette and stays.
        console.error("Error deleting the selection:", err);
        setDeleteErrorMessage(
          err?.response?.data?.message || "Deleting the selection failed.",
        );
      }
      handleCloseDeleteDialog();
      fetchData();
    }
  };

  // Files the selection in a project (null = unfiled).
  const handleMoveToProject = async (row, projectId) => {
    try {
      await moveItems([{ kind: "selection", id: row.id }], projectId);
      const name = projectId ? projects.find((p) => p.id === projectId)?.name || null : null;
      setPageState((old) => ({
        ...old,
        data: old.data.map((r) => (r.id === row.id ? { ...r, project: projectId, project_name: name } : r)),
      }));
      setDeleteErrorMessage(null);
      reloadProjects();
    } catch (error) {
      console.error("Moving the selection failed:", error);
      setDeleteErrorMessage(projectErrorMessage(error, "Moving the selection failed."));
    }
  };

  const filteredData = pageState.data.filter(
    (row) =>
      (typeFilter === ALL_KINDS || row.selection_type === typeFilter) &&
      matchesProjectFilter(row, projectFilter)
  );
  const filtered = projectFilter !== ALL_PROJECTS || typeFilter !== ALL_KINDS;

  const total = pageState.data.length;
  const panel = (
    <ResourcePanel
      bandSx={{ background: KIND.header }}
      overline={scope === LIST_SCOPE_MINE ? "Your selections" : "All selections"}
      // One count, in the panel only: what the list shows, "of" the total
      // while a filter narrows it.
      headline={
        pageState.isLoading
          ? "Loading"
          : filtered
            ? `${filteredData.length} of ${total} selections`
            : `${total} selection${total === 1 ? "" : "s"}`
      }
      subline={scope === LIST_SCOPE_MINE ? "Created by you" : "All accounts"}
      about="A selection filters the measured samples of explorations into a palette: resolution, marking time, repeatability and contrast, then the colours a design needs. Palettes feed the reproductions."
      filters={
        <>
          <PanelScopeRow value={scope} onChange={setScope} />
          <KindFilterSelect label="Type" options={TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} sx={{ minWidth: 0, width: "100%" }} />
          <ProjectFilterSelect projects={projects} value={projectFilter} onChange={setProjectFilter} sx={{ minWidth: 0, width: "100%" }} />
          {filtered ? (
            <PanelClearFilters
              onClick={() => {
                setTypeFilter(ALL_KINDS);
                setProjectFilter(ALL_PROJECTS);
              }}
            />
          ) : null}
        </>
      }
      actions={
        <>
          <PanelButton primary sx={KIND_BUTTON_SX} startIcon={<AddCircleOutlineIcon />} onClick={onNewClick}>
            New selection
          </PanelButton>
          <PanelButton
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchData();
              reloadProjects();
            }}
            disabled={pageState.isLoading}
          >
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Selections"
        loading={pageState.isLoading}
        loadingLabel="Loading selections…"
        error={
          deleteErrorMessage ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setDeleteErrorMessage(null)}>
              {deleteErrorMessage}
            </Alert>
          ) : null
        }
        count={filteredData.length}
        emptyText={
          filtered
            ? "No selections match the selected filters."
            : scope === LIST_SCOPE_MINE
              ? "You have no selections yet — create one."
              : "No selections yet — create the first one."
        }
        panel={panel}
        grid
      >
        {filteredData.map((row) => (
          <SelectionCard
            key={row.id}
            row={row}
            rights={rightsOf(row)}
            onContinue={() => onContinueClick(row)}
            onOverview={() => onOverviewClick(row)}
            onDelete={() => handleOpenDeleteDialog(row)}
            projects={projects}
            onMoveToProject={(projectId) => handleMoveToProject(row, projectId)}
          />
        ))}
      </ResourcePageLayout>

      {/* Delete confirmation: type the selection name to arm the button. */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete selection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the selection “{currentRow?.name}” with all of its
            filter results and marking files. This action cannot be undone.
          </DialogContentText>
          {currentRights?.warning && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {currentRights.warning}
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 2 }}
            label="Type the selection name to confirm"
            placeholder={currentRow?.name || ""}
            value={deleteNameInput}
            onChange={(event) => setDeleteNameInput(event.target.value)}
            error={deleteNameInput.length > 0 && !deleteArmed}
            helperText={
              deleteNameInput.length > 0 && !deleteArmed
                ? "Does not match the selection name"
                : "Enter the exact name to enable deletion"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={!deleteArmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ColorSelectionHome;
